// src/pages/IssuancePlan/modals/ReceiveWarehouseStepModal.tsx
// "Xác nhận vé từ nhà in" theo dạng step timeline (README.md mục 7):
//   Bước 1: Xác nhận Nhận vé từ Nhà in
//   Bước 2: Nhập số lượng vé thực tế nhận
//   Bước 3: Hoàn thành kế hoạch
// Hoàn thành bước trước là điều kiện để chuyển bước tiếp theo (chỉ hiện/bật control của bước hiện
// tại). 3 bước chỉ là điều hướng phía frontend — dữ liệu số lượng giữ tạm trên UI, chỉ gọi API
// receive-warehouse hiện có đúng 1 lần ở bước 3 (không đổi backend/state machine).

import React, { useEffect, useState } from "react";
import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import { IssuancePlan } from "../types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

interface ReceiveWarehouseStepModalProps {
  isOpen: boolean;
  plan: IssuancePlan | null;
  submitting: boolean;
  onSubmit: (quantity: number) => void | Promise<void>;
  onClose: () => void;
}

const formatNumber = (value?: number | null) => Number(value || 0).toLocaleString("vi-VN");

type Step = 1 | 2 | 3;

const STEP_LABELS: Record<Step, string> = {
  1: st('xac_nhan_nhan_ve_tu_nha_text'),
  2: st('nhap_so_luong_ve_thuc_te_text'),
  3: st('hoan_thanh_ke_hoach_text'),
};

const ReceiveWarehouseStepModal: React.FC<ReceiveWarehouseStepModalProps> = ({
  isOpen,
  plan,
  submitting,
  onSubmit,
  onClose,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    if (isOpen && plan) {
      setStep(1);
      setQuantity(String(plan.expectedPrintQuantity || ""));
    }
  }, [isOpen, plan]);

  if (!plan) return null;

  const handleSubmitStep3 = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = Number(quantity);
    if (!value || value <= 0) return;
    await onSubmit(value);
  };

  const renderStepIcon = (target: Step) => {
    if (step > target) return <i className="ri-checkbox-circle-fill text-success fs-5"></i>;
    if (step === target) return <span className="badge bg-info rounded-pill">{target}</span>;
    return <span className="badge bg-light text-muted rounded-pill">{target}</span>;
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader className="bg-light p-3" toggle={onClose}>
        {st('xac_nhan_ve_tu_nha_in_title')} {plan.planCode}
      </ModalHeader>
      <ModalBody>
        <div className="vstack gap-3">
          {([1, 2, 3] as Step[]).map((s) => (
            <div key={s} className={`d-flex align-items-start gap-2 ${step < s ? "opacity-50" : ""}`}>
              <div style={{ width: 24 }}>{renderStepIcon(s)}</div>
              <div className="flex-grow-1">
                <div className="fw-medium">{STEP_LABELS[s]}</div>

                {s === 1 && step === 1 && (
                  <div className="mt-2">
                    <Button color="warning" outline size="sm" onClick={() => setStep(2)} disabled={submitting}>
                      {st('xac_nhan_nhan_ve_tu_nha_text')}
                    </Button>
                  </div>
                )}

                {s === 2 && step === 2 && (
                  <div className="mt-2">
                    {Number(quantity) !== plan.expectedPrintQuantity && quantity !== "" && (
                      <Alert color="warning" className="py-2">
                        {st('so_luong_thuc_nhan_dang_khac_message')} {formatNumber(plan.expectedPrintQuantity)}.
                      </Alert>
                    )}
                    <FormGroup className="mb-2">
                      <Label>
                        {st('so_luong_thuc_nhan_label')} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        value={quantity === "" ? "" : Number(quantity).toLocaleString("vi-VN")}
                        onChange={(event) => setQuantity(event.target.value.replace(/\D/g, ""))}
                      />
                    </FormGroup>
                    <Button
                      color="warning"
                      outline
                      size="sm"
                      disabled={!quantity || Number(quantity) <= 0}
                      onClick={() => setStep(3)}
                    >
                      {st('tiep_theo_button')}
                    </Button>
                  </div>
                )}

                {s === 3 && step === 3 && (
                  <Form onSubmit={handleSubmitStep3} className="mt-2">
                    <div className="text-muted small mb-2">
                      {st('so_luong_thuc_nhan_text')} <span className="fw-semibold">{formatNumber(Number(quantity))}</span>
                    </div>
                    <Button color="warning" type="submit" size="sm" disabled={submitting}>
                      {submitting && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      )}
                      {st('hoan_thanh_ke_hoach_text')}
                    </Button>
                  </Form>
                )}
              </div>
            </div>
          ))}
        </div>
      </ModalBody>
      <div className="modal-footer">
        <div className="hstack gap-2 justify-content-end">
          <Button color="light" onClick={onClose} disabled={submitting}>
            {st('dong_button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReceiveWarehouseStepModal;
